import { useCallback } from 'react';

/**
 * useEffectiveHandlers Hook
 * 
 * Creates wrapped handlers that use customViewSettings handlers for all cases.
 * The customViewSettings handlers now handle both:
 * - Custom view active: saves to custom view
 * - Default view active: saves to default view settings (backend)
 */
const useEffectiveHandlers = ({
  activeCustomView,
  customViewSettings,
  // Default handlers (kept for backwards compatibility but mostly unused now)
  handleFiltersChange,
  handleToggleArchived,
  handleToggleActionableOnly,
  setShowNestedCampaigns,
  setTableColumnVisibility,
  setTodoElementVisibility,
  setLocalFlowViewVisibility,
  handleFlowViewVisibilityChangeInternal,
  toolbar,
  handleSortSettingChange,
  handleCardDisplaySettingsChange,
  handleSwimlaneSettingChange,
  setTodoViewMode,
  setTodoSwimlaneGroupBy,
  setLocalSprint,
}) => {
  // Always use customViewSettings handlers - they now handle both custom and default views
  // This ensures settings are saved to the backend for both cases
  
  const effectiveHandleFiltersChange = customViewSettings.handleFiltersChange;
  const effectiveHandleToggleArchived = customViewSettings.handleToggleArchived;
  const effectiveHandleToggleActionableOnly = customViewSettings.handleToggleActionableOnly;
  const effectiveSetShowNestedCampaigns = customViewSettings.handleShowNestedCampaignsChange;
  const effectiveSetTableColumnVisibility = customViewSettings.handleTableColumnVisibilityChange;
  const effectiveSetTodoElementVisibility = customViewSettings.handleTodoElementVisibilityChange;

  // For flow visibility, we need to update local state AND save via customViewSettings
  const effectiveOnFlowViewVisibilityChange = useCallback((newVisibility) => {
    // Update local state for immediate UI feedback
    setLocalFlowViewVisibility(newVisibility);
    // Save via customViewSettings (handles both custom and default views)
    customViewSettings.handleFlowViewVisibilityChange(newVisibility);
  }, [setLocalFlowViewVisibility, customViewSettings.handleFlowViewVisibilityChange]);

  const effectiveSetGanttSwimlane = customViewSettings.handleGanttSwimlaneChange;

  // Create wrapped handlers for sort and card display that work with setLocalSprint
  const effectiveHandleSortSettingChange = useCallback((view, newSetting) => {
    customViewSettings.handleSortSettingChange(view, newSetting, setLocalSprint);
  }, [customViewSettings, setLocalSprint]);

  const effectiveHandleCardDisplaySettingsChange = useCallback((view, newSettings) => {
    customViewSettings.handleCardDisplaySettingsChange(view, newSettings, setLocalSprint);
  }, [customViewSettings, setLocalSprint]);

  const effectiveHandleSwimlaneSettingChange = useCallback((newSetting) => {
    customViewSettings.handleSwimlaneSettingChange(newSetting, setLocalSprint);
  }, [customViewSettings, setLocalSprint]);

  const effectiveSetTodoViewMode = customViewSettings.handleTodoViewModeChange;
  const effectiveSetTodoSwimlaneGroupBy = customViewSettings.handleTodoSwimlaneGroupByChange;

  return {
    effectiveHandleFiltersChange,
    effectiveHandleToggleArchived,
    effectiveHandleToggleActionableOnly,
    effectiveSetShowNestedCampaigns,
    effectiveSetTableColumnVisibility,
    effectiveSetTodoElementVisibility,
    effectiveOnFlowViewVisibilityChange,
    effectiveSetGanttSwimlane,
    effectiveHandleSortSettingChange,
    effectiveHandleCardDisplaySettingsChange,
    effectiveHandleSwimlaneSettingChange,
    effectiveSetTodoViewMode,
    effectiveSetTodoSwimlaneGroupBy,
  };
};

export default useEffectiveHandlers;
